import React, { useEffect, useState } from "react";
import { Box, Grid, Typography, makeStyles } from "@material-ui/core";
import { get, size, filter, find } from "lodash";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";

import CircularProgress from "@material-ui/core/CircularProgress";
import { GalleryFormSectionHeading } from "./GalleryFormSectionHeading";
import { PhotographUploader } from "./form/PhotographUploader";
import { StyledButton } from "../components/CustomButtons/StyledButton";
import classNames from "classnames";
import { facialRecognitionList } from "v2_actions/facial_recognition";
import { galleryList } from "../v2_actions/gallery";
import { handleSubmitResult } from "../v2_actions/form";
import { photographList } from "../v2_actions/photograph";
import { processGalleryPhotographsList } from "v2_actions/process_gallery_photographs";
import { briefGalleryListForPhotoArt } from "v2_actions/brief_gallery";
import { sentrylog } from "../utils/sentry";
import { setEditingGallery, setAddingPhotos } from "../actions/gallery";
import { showSuccess } from "../v2_actions/error";
import { Modal } from "./layout/Modal";
import styles from "assets/jss/material-dashboard-react/components/sidebarStyle.js";

const useStyles = makeStyles(styles);

const modalContentStyle = {
  padding: 20,
  textAlign: "center",
};

const modalMessageStyle = {
  marginBottom: 20,
};

const modalButtonsStyle = {
  display: "flex",
  gap: 12,
  justifyContent: "center",
};

export const GalleryUploadForm = ({
  addingPhotosGallery,
  savedGalleryId,
  addPhotosMode,
  isEditorForPhotoArt,
  formProps,
  user,
  country,
  bulkUploadInProgress,
  bulkUploadStatus,
  bulkUploadProgress,
  onBulkUploadClick,
  fileUploadsInProgress,
  setFileUploadsInProgress,
  numFilesCompleted,
  numFilesSelectedForUpload,
  rejectedFiles,
  setRejectedFiles,
  setShowRejectionsModal,
}) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const history = useHistory();
  const { setValue, getValues } = formProps;

  const [savingInProgress, setSavingInProgress] = useState(false);
  const [galleryId, setGalleryId] = useState(null);
  const [failedPhotoUploads, setFailedPhotoUploads] = useState([]);
  const [isFailedPhotoUploadDialogOpenReason, setIsFailedPhotoUploadDialogOpenReason] = useState(null);
  const [showLargeGalleryWarning, setShowLargeGalleryWarning] = useState(false);

  const uploadGalleryId = addPhotosMode ? get(addingPhotosGallery, "id") : savedGalleryId;

  useEffect(() => {
    if (uploadGalleryId) {
      dispatch(photographList.fetchGalleryPhotographs(uploadGalleryId));
    }
  }, [uploadGalleryId]);

  const galleryPhotos = useSelector(() =>
    photographList.getGalleryPhotographs(),
  );

  const onCancel = () => {
    dispatch(galleryList.invalidateList());
    dispatch(galleryList.fetchListIfNeeded());
    dispatch(setEditingGallery(null));
    dispatch(setAddingPhotos(null));
  };

  const onClickCancel = () => {
    const hasRejectedFiles = rejectedFiles.length > 0;
    if (hasRejectedFiles) {
      setShowRejectionsModal(true);
    } else {
      onCancel();
      isEditorForPhotoArt
        ? history.push("/photographer/photo_art_gallery")
        : history.push("/photographer/map");
    }
  };

  const on_all_saved = function (
    gallery_id,
    { successes, failures, reason },
  ) {
    const hasFailures = size(failures) > 0;
    const unacceptedfilesizeFailure =
      hasFailures && reason === "unacceptedfilesize";

    if (unacceptedfilesizeFailure) {
      setIsFailedPhotoUploadDialogOpenReason(
        "Photos must be greater than 20kb and less than 5Mb. Please reload the photograph/s, leave the photo/s out of the gallery or edit the photograph to fit the parameters",
      );
      setFailedPhotoUploads(failures);
      sentrylog({
        messageType: "photoupload_complete_errors",
        debuginfo: { reason, failures },
        isError: true,
        user: user,
      });
      return;
    }

    setGalleryId(gallery_id);
    dispatch(
      processGalleryPhotographsList.processGalleryPhotographs(gallery_id),
    );
    dispatch(
      facialRecognitionList.indexGalleryForFacialRecognition(gallery_id),
    );

    if (hasFailures) {
      setIsFailedPhotoUploadDialogOpenReason(reason);
      setFailedPhotoUploads(failures);
    } else {
      showSuccess("Success", `${size(successes)} photos saved`);
      onCancel();
      history.push("/photographer/map/");
    }

    if (isEditorForPhotoArt) {
      onCancel();
      history.push("/photographer/photo_art_gallery");
    }
    setSavingInProgress(false);
    dispatch(galleryList.invalidateList());
    dispatch(photographList.invalidateList());

    dispatch(briefGalleryListForPhotoArt.invalidateList());
    dispatch(briefGalleryListForPhotoArt.fetchListIfNeeded());
  };

  const getExistingPhotoCount = () => {
    return get(addingPhotosGallery, "number_of_photos_in_gallery", 0);
  };

  const doUpload = () => {
    const photos = getValues("files");
    const uploadGalleryId = addPhotosMode ? get(addingPhotosGallery, "id") : savedGalleryId;

    setSavingInProgress(true);

    const on_file_upload_progress = function (file, status, errorInfo) {
      fileUploadsInProgress[file.path] = { status, errorInfo };
      setFileUploadsInProgress({ ...fileUploadsInProgress });
    };

    const start = Date.now();
    galleryList.savePhotosv2({
      files: photos,
      galleryPhotographs: galleryPhotos,
      galleryId: uploadGalleryId,
      countryCode: country.country_code,
      on_file_upload_progress: on_file_upload_progress,
    }).then((res) => {
      const duration = Date.now() - start;
      sentrylog({
        messageType: "photoupload_complete",
        debuginfo: { duration },
        user: user,
      });
      handleSubmitResult({
        res,
        formProps,
        on_ok: (res) => {
          on_all_saved(uploadGalleryId, res);
        },
      });
    });
  };

  const onUploadSave = async () => {
    const photos = getValues("files");
    if (!photos || size(photos) === 0) {
      showSuccess("Done", "No photos to upload");
      onCancel();
      history.push("/photographer/map/");
      return;
    }

    const existingCount = getExistingPhotoCount();
    const photosNotInGallery = filter(photos, (photo) =>
      !find(galleryPhotos, (galleryPhoto) => photo.name === galleryPhoto.photo_name)
    );
    const totalCount = existingCount + size(photosNotInGallery);

    if (totalCount > 4000) {
      alert("Maximum number of photos per gallery is 4000");
      return;
    }

    if (totalCount > 3000 && !showLargeGalleryWarning) {
      setShowLargeGalleryWarning(true);
      return;
    }

    setShowLargeGalleryWarning(false);
    doUpload();
  };

  return (
    <>
      <Grid container className={classes.rightSideBarWrapper}>
        <Grid item xs={12}>
          <div
            className={classNames(
              classes.sidebarHeading,
              classes.galleryEditorSideBarHeading,
            )}
          >
            {`Upload Photos to ${get(addingPhotosGallery, "session_name", "Gallery")}`}
          </div>
        </Grid>
        {savingInProgress && (
          <Grid item xs={12} style={{ textAlign: "center" }}>
            <div className={classes.circularProgressWrapper}>
              <div className={classes.circularProgressWhell}>
                <CircularProgress
                  size={50}
                  mode="indeterminate"
                  style={{ color: "green" }}
                />
              </div>
            </div>
          </Grid>
        )}
        <Box className={classes.uploadPhotosHeadingWrapper}>
          <GalleryFormSectionHeading heading="Upload Photos" />
          <button
            type="button"
            className={`bulk-upload-button ${bulkUploadInProgress ? 'in-progress' : ''}`}
            style={{
              padding: '4px 8px',
              fontSize: '10px',
              backgroundColor: '#0c59cf',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              minWidth: bulkUploadInProgress ? '120px' : 'auto',
            }}
            onClick={onBulkUploadClick}
            disabled={bulkUploadInProgress}
          >
            {bulkUploadStatus === 'saving_gallery' && 'Saving gallery...'}
            {bulkUploadStatus === 'gallery_created' && 'Gallery created...'}
            {bulkUploadStatus === 'uploading' && `${bulkUploadProgress.uploaded} / ${bulkUploadProgress.total}`}
            {bulkUploadStatus === 'registering' && 'Registering photos...'}
            {!bulkUploadStatus && 'Bulk'}
          </button>
        </Box>
        <Grid item xs={12} className={classes.photographUploaderWrapper}>
          <PhotographUploader
            savingInProgress={savingInProgress}
            numFilesCompleted={numFilesCompleted}
            fileAnnotations={fileUploadsInProgress}
            maxFileLimit={isEditorForPhotoArt ? 1 : null}
            isEditorForPhotoArt={isEditorForPhotoArt}
            numFilesSelectedForUpload={numFilesSelectedForUpload}
            uploadMessage={
              isEditorForPhotoArt ? "Add Photo" : "Add photos to gallery"
            }
            onChange={({ files, rejections }) => {
              setValue("files", files, { shouldValidate: true })
              setRejectedFiles(rejections)
            }}
            setShowRejectionsModal={setShowRejectionsModal}
            setRejectedFiles={setRejectedFiles}
            rejectedFiles={rejectedFiles}
            validate={true}
          />
        </Grid>
        <Grid item xs={6} className={classes.buttonWrapper}>
          <StyledButton
            className={classes.grayButton}
            onClick={onClickCancel}
          >
            Cancel
          </StyledButton>
        </Grid>
        <Grid item xs={6} className={classes.buttonWrapper}>
              <StyledButton
                className={savingInProgress ? classes.grayButton : ""}
                disabled={savingInProgress}
                onClick={onUploadSave}
              >
                Upload Photos
              </StyledButton>
        </Grid>
      </Grid>
      {showLargeGalleryWarning && (
        <Modal
          title="Large Gallery Warning"
          onClose={() => setShowLargeGalleryWarning(false)}
        >
          <Box style={modalContentStyle}>
            <Typography variant="subtitle1" style={modalMessageStyle}>
              This is a large gallery. Are you sure you don't want to split it into two separate galleries?
            </Typography>
            <Box style={modalButtonsStyle}>
              <StyledButton
                onClick={() => {
                  setShowLargeGalleryWarning(false);
                  doUpload();
                }}
              >
                No, Continue
              </StyledButton>
              <StyledButton
                className={classes.grayButton}
                onClick={() => {
                  setShowLargeGalleryWarning(false);
                }}
              >
                Yes, Cancel
              </StyledButton>
            </Box>
          </Box>
        </Modal>
      )}
    </>
  );
};
